import type { ZodError, ZodIssue, ZodTypeAny } from 'zod';
import type { FormattedZodIssue, RepeatedFieldSummary, ValidateWithZodOptions, ZodIssueSummary, ZodSchemaDef, ZodValidationSource } from './zod-error-types';
import { MAX_SCHEMA_KEYS_IN_LABEL, MAX_SUMMARY_ITEMS, ZOD_PARSED_TYPE_LABELS } from './zod-api-constants';

function getIssuePath(issue: ZodIssue) {
  return issue.path.length ? issue.path.join('.') : 'root';
}

function sanitizeIssueValue(value: unknown): unknown {
  if (value === undefined) {
    return undefined;
  }

  if (value === null) {
    return 'null';
  }

  if (typeof value === 'string') {
    return ZOD_PARSED_TYPE_LABELS.has(value) ? value : '[redacted:string]';
  }

  if (Array.isArray(value)) {
    return `[redacted:array(${value.length})]`;
  }

  return `[redacted:${typeof value}]`;
}

function getIssueExpected(issue: ZodIssue) {
  return 'expected' in issue ? sanitizeIssueValue(issue.expected) : undefined;
}

function getIssueReceived(issue: ZodIssue) {
  return 'received' in issue ? sanitizeIssueValue(issue.received) : undefined;
}

function sanitizeIssueMessage(message: string) {
  return message
    .replace(/'[^']*'/g, '\'[redacted]\'')
    .replace(/"[^"]*"/g, '"[redacted]"');
}

function formatZodIssue(issue: ZodIssue): FormattedZodIssue {
  return {
    code: issue.code,
    expected: getIssueExpected(issue),
    message: sanitizeIssueMessage(issue.message),
    path: getIssuePath(issue),
    received: getIssueReceived(issue),
  };
}

function formatZodIssues(err: ZodError) {
  return err.issues.map(formatZodIssue);
}

function getListIssueParts(path: string) {
  const parts = path.split('.');
  const indexPosition = parts.findIndex(part => /^\d+$/.test(part));

  if (indexPosition === -1) {
    return null;
  }

  return {
    fieldPath: parts.slice(indexPosition + 1).join('.') || 'root',
    index: Number(parts[indexPosition]),
    listPath: parts.slice(0, indexPosition).join('.') || 'root',
  };
}

function getLimitedSummaryValues<T>(values: T[]): Array<T | string> {
  return values.length > MAX_SUMMARY_ITEMS
    ? [...values.slice(0, MAX_SUMMARY_ITEMS), `+${values.length - MAX_SUMMARY_ITEMS} more`]
    : values;
}

function getTopRepeatedFields(values: RepeatedFieldSummary[]) {
  return values.slice(0, MAX_SUMMARY_ITEMS);
}

function summarizeListIssues(issues: FormattedZodIssue[]) {
  // Zod reports list issues per item (`items.0.name`, `items.1.name`, ...).
  // This summary groups those repeated paths so list/pagination responses show
  // the affected indexes and repeated fields before the raw issue dump.
  const summariesByListPath = new Map<string, {
    fieldMessages: Map<string, Set<string>>;
    fieldOccurrences: Map<string, number>;
    indexes: Set<number>;
    issueCount: number;
    missingFields: Set<string>;
  }>();

  issues.forEach((issue) => {
    const parts = getListIssueParts(issue.path);

    if (!parts) {
      return;
    }

    const summary = summariesByListPath.get(parts.listPath) ?? {
      fieldMessages: new Map<string, Set<string>>(),
      fieldOccurrences: new Map<string, number>(),
      indexes: new Set<number>(),
      issueCount: 0,
      missingFields: new Set<string>(),
    };

    summary.indexes.add(parts.index);
    summary.issueCount += 1;
    summary.fieldOccurrences.set(parts.fieldPath, (summary.fieldOccurrences.get(parts.fieldPath) ?? 0) + 1);

    const messages = summary.fieldMessages.get(parts.fieldPath) ?? new Set<string>();
    messages.add(issue.message);
    summary.fieldMessages.set(parts.fieldPath, messages);

    if (issue.code === 'invalid_type' && issue.received === 'undefined') {
      summary.missingFields.add(parts.fieldPath);
    }

    summariesByListPath.set(parts.listPath, summary);
  });

  return Array.from(summariesByListPath.entries()).map<ZodIssueSummary>(([listPath, summary]) => ({
    affectedIndexes: getLimitedSummaryValues(Array.from(summary.indexes).sort((a, b) => a - b)),
    issueCount: summary.issueCount,
    listPath,
    missingFields: getLimitedSummaryValues(Array.from(summary.missingFields).sort()),
    repeatedFields: getTopRepeatedFields(
      Array.from(summary.fieldOccurrences.entries())
        .map(([field, count]) => ({
          count,
          field,
          messages: Array.from(summary.fieldMessages.get(field) ?? []),
        }))
        .sort((a, b) => b.count - a.count),
    ),
  }));
}

function getValidationDetails(issues: FormattedZodIssue[]) {
  const listIssueSummary = summarizeListIssues(issues);

  return {
    issues,
    listIssueSummary,
  };
}

function getZodDef(schema: ZodTypeAny): ZodSchemaDef {
  return schema._def as ZodSchemaDef;
}

function getZodTypeLabel(schema: ZodTypeAny) {
  return getZodDef(schema).typeName?.replace(/^Zod/, '') ?? schema.constructor.name;
}

function getObjectShape(schema: ZodTypeAny) {
  const shape = getZodDef(schema).shape;

  return typeof shape === 'function' ? shape() : undefined;
}

function isPaginationShape(shape: Record<string, ZodTypeAny>) {
  // PaginationSchema(...) instances are created inline in many API files, so
  // they often have no `.describe(...)`. Detecting this shape gives logs a
  // useful `Pagination<ItemSchema>` label without touching every call site.
  return ['items', 'totalItems', 'totalPages', 'page', 'hasPrevPage', 'hasNextPage']
    .every(key => key in shape);
}

function getObjectLabel(schema: ZodTypeAny, depth: number) {
  const shape = getObjectShape(schema);

  if (!shape) {
    return getZodTypeLabel(schema);
  }

  if (isPaginationShape(shape)) {
    const itemsSchema = shape.items;
    const itemSchema = itemsSchema ? getZodDef(itemsSchema).type : undefined;

    return itemSchema
      ? `Pagination<${getSchemaDebugName(itemSchema, depth + 1)}>`
      : 'Pagination<Unknown>';
  }

  const keys = Object.keys(shape);
  const visibleKeys = keys.slice(0, MAX_SCHEMA_KEYS_IN_LABEL).join(', ');
  const rest = keys.length > MAX_SCHEMA_KEYS_IN_LABEL ? ', ...' : '';

  return `Object{${visibleKeys}${rest}}`;
}

function getSchemaDebugName(schema: ZodTypeAny, depth = 0): string {
  // JavaScript cannot read the variable name (`GameDtoSchema`) at runtime.
  // `.describe('GameDtoSchema')` is the most reliable explicit name; otherwise
  // we infer a compact structural label from the Zod schema internals.
  if (schema.description) {
    return schema.description;
  }

  if (depth > 2) {
    return getZodTypeLabel(schema);
  }

  const typeName = getZodTypeLabel(schema);

  if (typeName === 'Array') {
    const itemSchema = getZodDef(schema).type;

    return itemSchema ? `Array<${getSchemaDebugName(itemSchema, depth + 1)}>` : 'Array<Unknown>';
  }

  if (typeName === 'Object') {
    return getObjectLabel(schema, depth);
  }

  return typeName;
}

function getCallerLocation() {
  const stack = new Error('Zod validation caller').stack;

  // The caller is a fallback breadcrumb for schemas that do not have explicit
  // names yet. It usually points to the API method that called validateWithZod.
  return stack
    ?.split('\n')
    .map(line => line.trim())
    .find(line => !line.includes('zod.utils') && !line.startsWith('Error'));
}

function getValidationOptions(options: ZodValidationSource | ValidateWithZodOptions): Required<Pick<ValidateWithZodOptions, 'from'>> & Omit<ValidateWithZodOptions, 'from'> {
  if (typeof options === 'string') {
    return { from: options };
  }

  return {
    from: 'backend',
    ...options,
  };
}

function getValidationContext(schema: ZodTypeAny, options: ReturnType<typeof getValidationOptions>, issueCount?: number) {
  return {
    caller: getCallerLocation(),
    endpoint: options.endpoint,
    from: options.from,
    issueCount,
    schema: options.schemaName ?? getSchemaDebugName(schema),
  };
}

function logUnexpectedValidationError(schema: ZodTypeAny, options: ReturnType<typeof getValidationOptions>, err: unknown) {
  const validationContext = getValidationContext(schema, options);
  const title = `[Zod validation crashed] ${validationContext.schema}`;

  console.error(title, validationContext, err);
}

export { formatZodIssues, getValidationContext, getValidationDetails, getValidationOptions, logUnexpectedValidationError };
